import React, { ReactNode } from 'react'

export interface IconProps {
    size?: number,
    className?: string
}

// Line icons drawn on a 24px grid (Lucide-style); stroke follows the current text color
const createIcon = (paths: ReactNode) => ({ size = 18, className }: IconProps) =>
    <svg
        width={size}
        height={size}
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        className={className}
        aria-hidden='true'
        focusable='false'
    >
        {paths}
    </svg>

export const EyeIcon = createIcon(<>
    <path d='M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z' />
    <circle cx='12' cy='12' r='3' />
</>);

export const DownloadIcon = createIcon(<>
    <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
    <path d='m7 10 5 5 5-5' />
    <path d='M12 15V3' />
</>);

export const ListPlusIcon = createIcon(<>
    <path d='M11 12H3' />
    <path d='M16 6H3' />
    <path d='M16 18H3' />
    <path d='M18 9v6' />
    <path d='M21 12h-6' />
</>);

export const ChevronLeftIcon = createIcon(<path d='m15 18-6-6 6-6' />);

export const ChevronRightIcon = createIcon(<path d='m9 18 6-6-6-6' />);

export const ChevronsLeftIcon = createIcon(<>
    <path d='m11 17-5-5 5-5' />
    <path d='m18 17-5-5 5-5' />
</>);

export const ChevronsRightIcon = createIcon(<>
    <path d='m6 17 5-5-5-5' />
    <path d='m13 17 5-5-5-5' />
</>);

export const TrashIcon = createIcon(<>
    <path d='M3 6h18' />
    <path d='M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6' />
    <path d='M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2' />
</>);

export const FlagIcon = createIcon(<>
    <path d='M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z' />
    <path d='M4 22v-7' />
</>);

export const ShareIcon = createIcon(<>
    <circle cx='18' cy='5' r='3' />
    <circle cx='6' cy='12' r='3' />
    <circle cx='18' cy='19' r='3' />
    <path d='m8.59 13.51 6.83 3.98' />
    <path d='m15.41 6.51-6.82 3.98' />
</>);

export const EraserIcon = createIcon(<>
    <path d='m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21' />
    <path d='M22 21H7' />
    <path d='m5 11 9 9' />
</>);

export const ImageIcon = createIcon(<>
    <rect width='18' height='18' x='3' y='3' rx='2' ry='2' />
    <circle cx='9' cy='9' r='2' />
    <path d='m21 15-3.09-3.09a2 2 0 0 0-2.82 0L6 21' />
</>);

export const FileTextIcon = createIcon(<>
    <path d='M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z' />
    <path d='M14 2v4a2 2 0 0 0 2 2h4' />
    <path d='M10 9H8' />
    <path d='M16 13H8' />
    <path d='M16 17H8' />
</>);

export const MaximizeIcon = createIcon(<>
    <path d='M8 3H5a2 2 0 0 0-2 2v3' />
    <path d='M21 8V5a2 2 0 0 0-2-2h-3' />
    <path d='M3 16v3a2 2 0 0 0 2 2h3' />
    <path d='M16 21h3a2 2 0 0 0 2-2v-3' />
</>);

export const CloseIcon = createIcon(<>
    <path d='M18 6 6 18' />
    <path d='m6 6 12 12' />
</>);

export const ChevronUpIcon = createIcon(<path d='m18 15-6-6-6 6' />);

export const ChevronDownIcon = createIcon(<path d='m6 9 6 6 6-6' />);

export const ChevronsUpIcon = createIcon(<>
    <path d='m17 11-5-5-5 5' />
    <path d='m17 18-5-5-5 5' />
</>);

export const ChevronsDownIcon = createIcon(<>
    <path d='m7 6 5 5 5-5' />
    <path d='m7 13 5 5 5-5' />
</>);

export const ListIcon = createIcon(<>
    <path d='M3 12h.01' />
    <path d='M3 18h.01' />
    <path d='M3 6h.01' />
    <path d='M8 12h13' />
    <path d='M8 18h13' />
    <path d='M8 6h13' />
</>);

export const GalleryIcon = createIcon(<>
    <rect width='18' height='14' x='3' y='3' rx='2' />
    <path d='M4 21h1' />
    <path d='M9 21h1' />
    <path d='M14 21h1' />
    <path d='M19 21h1' />
</>);

export const GripIcon = createIcon(<>
    <circle cx='9' cy='5' r='1' />
    <circle cx='9' cy='12' r='1' />
    <circle cx='9' cy='19' r='1' />
    <circle cx='15' cy='5' r='1' />
    <circle cx='15' cy='12' r='1' />
    <circle cx='15' cy='19' r='1' />
</>);

export const SpinnerIcon = ({ size = 18, className }: IconProps) =>
    <svg width={size} height={size} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' className={className} aria-hidden='true' focusable='false'>
        <path d='M21 12a9 9 0 1 1-6.22-8.56'>
            <animateTransform attributeName='transform' type='rotate' from='0 12 12' to='360 12 12' dur='0.8s' repeatCount='indefinite' />
        </path>
    </svg>
